import { type BoxRect, type GeometryResolver } from '../types.ts';

/**
 * Adapts scroll-content geometry for a horizontally fixed highlight surface.
 *
 * The base resolver includes `scrollLeft` in X so ordinary highlights move
 * with table content. A fixed surface already cancels that movement through
 * native sticky positioning, so using the base X unchanged would apply the
 * horizontal offset twice. Removing `scrollLeft` converts X into the fixed
 * surface's coordinate space. Y is intentionally unchanged so highlights
 * continue to scroll vertically with their cells.
 */
export class FixedHorizontalGeometryResolver implements GeometryResolver {
  readonly #baseResolver: GeometryResolver;

  constructor(baseResolver: GeometryResolver) {
    this.#baseResolver = baseResolver;
  }

  getCellBounds(rowIndex: number, colIndex: number): BoxRect | null {
    return this.#fixX(this.#baseResolver.getCellBounds(rowIndex, colIndex));
  }

  getRowBounds(rowIndex: number): BoxRect | null {
    return this.#fixX(this.#baseResolver.getRowBounds(rowIndex));
  }

  getColumnBounds(colIndex: number): BoxRect | null {
    return this.#fixX(this.#baseResolver.getColumnBounds(colIndex));
  }

  getContainerBounds(): BoxRect | null {
    return this.#fixX(this.#baseResolver.getContainerBounds());
  }

  getScrollContainer(): HTMLElement | null {
    return this.#baseResolver.getScrollContainer();
  }

  #fixX(bounds: BoxRect | null): BoxRect | null {
    if (!bounds) {
      return null;
    }

    return {
      ...bounds,
      x: bounds.x - (this.getScrollContainer()?.scrollLeft ?? 0)
    };
  }
}
